import { useQuery } from "@tanstack/react-query";
import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
  AccessibilityFilters,
  BusinessNote,
  BusinessNoteAction,
  BusinessNoteTask,
  businessNotesAll,
  ComplexCategoriesData,
  VenueDetails,
} from "../../../types";
import { venueQueryOptions } from "./venueDetailsQueryOptions";

type UseVenueDetailsBySlugProps = {
  slug: string;
  /** Optional; omitted or empty when location is not needed (e.g. business portal). */
  latitude?: string;
  longitude?: string;
  accessToken: string;
  enabled: boolean;
  type?: "Basic" | "Complex";
  syncSpaces?: boolean;
  password?: string;
  passwordToken?: string;
  ssoToken?: string;
};

export type VenueDetailsBySlugResponse = {
  details: VenueDetails;
  accesibilityCategories?: AccessibilityCategory[]; // Only for Basic venues
  accesibilityCategoriesHybrid?: AccessibilityCategoryHybrid[]; // Deprecated - hybrid venues now use Enterprise data
  accesibilityCategoriesEnterprise?: ComplexCategoriesData[]; // For Complex (Hybrid + Enterprise) venues
  floorTypeOptions?: { id: number; title: string }[]; // For Complex venues
  enterpriseBusinessNotesActions?: BusinessNoteAction[]; // For Complex venues
  businessNotesTasks?: {
    Custom: BusinessNoteTask[];
    General: BusinessNoteTask[];
  }; // For Complex venues
  review_tags?: ComplexCategoriesData[]; // For Complex venues
  businessNotesAreaLocationTypes?: string[]; // For Complex venues
  businessNotesForMappers?: BusinessNote[]; // For Complex venues
  businessNotesAll?: businessNotesAll[]; // For Complex venues
  accessibility_filters?: AccessibilityFilters; // For Complex venues
  canViewPage?: number;
  passwordProtectedPageToken?: string;
  isSsoProtect?: number;
  ssoTenantId?: string;
};

export function useVenueDetailsBySlug({
  slug,
  latitude = "",
  longitude = "",
  accessToken,
  enabled,
  type,
  syncSpaces = true,
  password,
  passwordToken,
  ssoToken,
}: UseVenueDetailsBySlugProps) {
  const options = venueQueryOptions({
    slug,
    latitude,
    longitude,
    accessToken,
    type,
    syncSpaces,
    password,
    passwordToken,
    ssoToken,
  });

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    ...options,
    enabled,
  });

  return {
    data,
    error,
    isLoading: isLoading || isFetching,
    refetch,
  };
}
